import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Axios default header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const fetchUser = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      // Only treat 401 as genuine session invalidation — network timeouts / 500s
      // should not sign the user out (prevents rapid-logout bug on flaky networks).
      if (err.response && err.response.status === 401) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUser(); /* eslint-disable-next-line */ }, [token]);

  // Exchange Emergent session_id for a long-lived session_token + user
  const loginWithGoogleSession = async (sessionId) => {
    // Capture the browser fingerprint so the backend can enforce the
    // "2 accounts per device / 24h" cap. Never blocks sign-in if it fails.
    const deviceFingerprint = await getDeviceFingerprint();
    const res = await axios.post(`${API}/auth/google/session`, {
      session_id: sessionId,
      device_fingerprint: deviceFingerprint || undefined,
    });
    const { token: newToken, user: newUser, is_new_user } = res.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(newUser);
    return { user: newUser, isNewUser: is_new_user };
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = fetchUser;

  return (
    <AuthContext.Provider value={{ user, token, loading, loginWithGoogleSession, logout, refreshUser, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
